import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
  Badge,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  TrendingUp,
  People,
  ShoppingCart,
  AttachMoney,
  Inventory,
  Receipt,
  CalendarToday,
  Warning,
  AddCircle,
  PersonAdd,
  PostAdd,
  ReceiptLong,
  Notifications,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { format, addDays, isAfter, isBefore, parseISO, subMonths, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useResponsive } from '../hooks/useResponsive';

const COLORS = ['#003366', '#D4AF37', '#4A90E2', '#50C878'];

const StatCard = ({ title, value, icon, color, subtitle, onClick }) => (
  <Card sx={{ cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box flex={1}>
          <Typography color="text.secondary" variant="body2" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" component="div" fontWeight="bold" color={color}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}.lighter`,
            borderRadius: 2,
            p: 1.5,
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartsExpanded, setChartsExpanded] = useState(!isMobile);
  const [activityExpanded, setActivityExpanded] = useState(!isMobile);

  // Raw data
  const [sales, setSales] = useState([]);
  const [leads, setLeads] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [events, setEvents] = useState([]);

  // Calculated metrics
  const [stats, setStats] = useState({
    activeSales: 0,
    monthlyRevenue: 0,
    inventoryCount: 0,
    inventoryValue: 0,
    activeLeads: 0,
    upcomingEvents: 0,
    outstandingInvoices: 0,
  });

  const [salesPipeline, setSalesPipeline] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [upcomingEventsList, setUpcomingEventsList] = useState([]);
  const [alerts, setAlerts] = useState({
    lowInventory: [],
    overdueInvoices: [],
    followUpLeads: [],
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [salesRes, leadsRes, inventoryRes, invoicesRes, eventsRes] = await Promise.all([
        api.get('/sales/').catch(() => ({ data: [] })),
        api.get('/leads/').catch(() => ({ data: [] })),
        api.get('/inventory/items').catch(() => ({ data: [] })),
        api.get('/invoices/').catch(() => ({ data: [] })),
        api.get('/calendar/events').catch(() => ({ data: [] })),
      ]);

      const salesData = salesRes.data?.sales || salesRes.data || [];
      const leadsData = leadsRes.data?.leads || leadsRes.data || [];
      const inventoryData = inventoryRes.data?.items || inventoryRes.data || [];
      const invoicesData = invoicesRes.data?.invoices || invoicesRes.data || [];
      const eventsData = eventsRes.data?.events || eventsRes.data || [];

      setSales(salesData);
      setLeads(leadsData);
      setInventory(inventoryData);
      setInvoices(invoicesData);
      setEvents(eventsData);

      calculateMetrics(salesData, leadsData, inventoryData, invoicesData, eventsData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please refresh the page.');
      setLoading(false);
    }
  };

  const calculateMetrics = (salesData, leadsData, inventoryData, invoicesData, eventsData) => {
    // Current month date range
    const now = new Date();
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(now);

    // Active sales
    const activeSales = salesData.filter(s => s.status === 'active' || s.status === 'in_progress').length;

    // Monthly revenue (completed sales this month)
    const monthlyRevenue = salesData
      .filter(s => {
        if (s.status !== 'completed' || !s.created_at) return false;
        const saleDate = parseISO(s.created_at);
        return isWithinInterval(saleDate, { start: monthStart, end: monthEnd });
      })
      .reduce((sum, s) => sum + (parseFloat(s.total_revenue) || 0), 0);

    // Inventory stats
    const availableInventory = inventoryData.filter(i => i.status === 'available');
    const inventoryCount = availableInventory.length;
    const inventoryValue = availableInventory.reduce((sum, i) => sum + (parseFloat(i.price) || 0), 0);

    // Active leads (not closed or converted)
    const activeLeads = leadsData.filter(l => !['closed', 'lost'].includes(l.status)).length;

    // Upcoming events (next 7 days)
    const next7Days = addDays(now, 7);
    const upcomingEvents = eventsData.filter(e => {
      if (!e.start_time) return false;
      const eventDate = parseISO(e.start_time);
      return isAfter(eventDate, now) && isBefore(eventDate, next7Days);
    }).length;

    // Outstanding invoices
    const outstandingInvoices = invoicesData
      .filter(inv => inv.status === 'pending' || inv.status === 'sent')
      .reduce((sum, inv) => sum + (parseFloat(inv.total_amount) || 0), 0);

    setStats({
      activeSales,
      monthlyRevenue,
      inventoryCount,
      inventoryValue,
      activeLeads,
      upcomingEvents,
      outstandingInvoices,
    });

    // Calculate sales pipeline
    calculateSalesPipeline(salesData);

    // Calculate revenue trend
    calculateRevenueData(salesData);

    // Calculate recent activity
    calculateRecentActivity(salesData, leadsData, eventsData, inventoryData);

    // Calculate upcoming events
    calculateUpcomingEvents(eventsData);

    // Calculate alerts
    calculateAlerts(inventoryData, invoicesData, leadsData);
  };

  const calculateSalesPipeline = (salesData) => {
    const pipeline = [
      { name: 'Scheduled', count: salesData.filter(s => s.status === 'scheduled').length },
      { name: 'In Progress', count: salesData.filter(s => s.status === 'in_progress' || s.status === 'active').length },
      { name: 'Completed', count: salesData.filter(s => s.status === 'completed').length },
    ];
    setSalesPipeline(pipeline);
  };

  const calculateRevenueData = (salesData) => {
    const monthsData = [];
    for (let i = 5; i >= 0; i--) {
      const date = subMonths(new Date(), i);
      const monthStart = startOfMonth(date);
      const monthEnd = endOfMonth(date);

      const monthSales = salesData.filter(s => {
        if (!s.created_at || s.status !== 'completed') return false;
        const saleDate = parseISO(s.created_at);
        return isWithinInterval(saleDate, { start: monthStart, end: monthEnd });
      });

      const revenue = monthSales.reduce((sum, s) => sum + (parseFloat(s.total_revenue) || 0), 0);

      monthsData.push({
        month: format(date, 'MMM yyyy'),
        revenue,
      });
    }
    setRevenueData(monthsData);
  };

  const calculateRecentActivity = (salesData, leadsData, eventsData, inventoryData) => {
    const activities = [];

    // Recent sales
    salesData.slice(0, 3).forEach(sale => {
      activities.push({
        id: `sale-${sale.id}`,
        type: 'sale',
        title: `New Sale: ${sale.client_name || 'Estate Sale'}`,
        subtitle: `Status: ${sale.status}`,
        date: sale.created_at,
        icon: <ShoppingCart sx={{ color: 'primary.main' }} />,
      });
    });

    // Recent leads
    leadsData.slice(0, 3).forEach(lead => {
      activities.push({
        id: `lead-${lead.id}`,
        type: 'lead',
        title: `New Lead: ${lead.full_name}`,
        subtitle: `Status: ${lead.status}`,
        date: lead.created_at,
        icon: <People sx={{ color: 'warning.main' }} />,
      });
    });

    // Recent inventory
    inventoryData.slice(0, 2).forEach(item => {
      activities.push({
        id: `inventory-${item.id}`,
        type: 'inventory',
        title: `Added: ${item.name}`,
        subtitle: `$${parseFloat(item.price || 0).toLocaleString()}`,
        date: item.created_at,
        icon: <Inventory sx={{ color: 'info.main' }} />,
      });
    });

    // Upcoming events
    eventsData.slice(0, 2).forEach(event => {
      activities.push({
        id: `event-${event.id}`,
        type: 'event',
        title: event.title,
        subtitle: event.event_type || 'Event',
        date: event.start_time,
        icon: <CalendarToday sx={{ color: 'secondary.main' }} />,
      });
    });

    activities.sort((a, b) => new Date(b.date) - new Date(a.date));
    setRecentActivity(activities.slice(0, 10));
  };

  const calculateUpcomingEvents = (eventsData) => {
    const now = new Date();
    const upcoming = eventsData
      .filter(e => {
        if (!e.start_time) return false;
        const eventDate = parseISO(e.start_time);
        return isAfter(eventDate, now);
      })
      .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
      .slice(0, 5);
    setUpcomingEventsList(upcoming);
  };

  const calculateAlerts = (inventoryData, invoicesData, leadsData) => {
    // Low inventory alert (items priced below average)
    const avgPrice = inventoryData.reduce((sum, i) => sum + (parseFloat(i.price) || 0), 0) / inventoryData.length;
    const lowInventory = inventoryData.filter(i => parseFloat(i.price) < avgPrice * 0.5 && i.status === 'available').slice(0, 5);

    // Overdue invoices (sent more than 30 days ago)
    const now = new Date();
    const overdueInvoices = invoicesData.filter(inv => {
      if (!inv.created_at || inv.status === 'paid') return false;
      const invoiceDate = parseISO(inv.created_at);
      const daysDiff = (now - invoiceDate) / (1000 * 60 * 60 * 24);
      return daysDiff > 30;
    }).slice(0, 5);

    // Leads requiring follow-up (contacted but not qualified)
    const followUpLeads = leadsData.filter(l => l.status === 'contacted').slice(0, 5);

    setAlerts({
      lowInventory,
      overdueInvoices,
      followUpLeads,
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={{ xs: 2, sm: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Dashboard Overview
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
          Welcome back! Here's what's happening with your estate sales today.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Top Row - Key Metrics Cards */}
      <Grid container spacing={{ xs: 2, sm: 3 }} mb={{ xs: 2, sm: 3 }}>
        <Grid item xs={6} sm={6} md={4} lg={2}>
          <StatCard
            title="Active Sales"
            value={stats.activeSales}
            icon={<ShoppingCart sx={{ color: 'primary.main' }} />}
            color="primary.main"
            subtitle={!isMobile ? "Currently running" : ""}
            onClick={() => navigate('/sales')}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={4} lg={2}>
          <StatCard
            title={isMobile ? "Revenue" : "Revenue (Month)"}
            value={`$${stats.monthlyRevenue.toLocaleString()}`}
            icon={<AttachMoney sx={{ color: 'success.main' }} />}
            color="success.main"
            subtitle={!isMobile ? "This month" : ""}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={4} lg={2}>
          <StatCard
            title="Inventory"
            value={stats.inventoryCount}
            icon={<Inventory sx={{ color: 'info.main' }} />}
            color="info.main"
            subtitle={`$${stats.inventoryValue.toLocaleString()}`}
            onClick={() => navigate('/inventory')}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={4} lg={2}>
          <StatCard
            title="Leads"
            value={stats.activeLeads}
            icon={<People sx={{ color: 'warning.main' }} />}
            color="warning.main"
            subtitle={!isMobile ? "Requires follow-up" : ""}
            onClick={() => navigate('/leads')}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={4} lg={2}>
          <StatCard
            title="Events"
            value={stats.upcomingEvents}
            icon={<CalendarToday sx={{ color: 'secondary.main' }} />}
            color="secondary.main"
            subtitle={!isMobile ? "Next 7 days" : ""}
            onClick={() => navigate('/calendar')}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={4} lg={2}>
          <StatCard
            title="Outstanding"
            value={`$${stats.outstandingInvoices.toLocaleString()}`}
            icon={<Receipt sx={{ color: 'error.main' }} />}
            color="error.main"
            subtitle={!isMobile ? "Unpaid invoices" : ""}
            onClick={() => navigate('/invoices')}
          />
        </Grid>
      </Grid>

      {/* Second Row - Charts */}
      <Card sx={{ mb: { xs: 2, sm: 3 } }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6">
              Charts & Analytics
            </Typography>
            {isMobile && (
              <IconButton onClick={() => setChartsExpanded(!chartsExpanded)} size="small">
                {chartsExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}
          </Box>
          <Collapse in={chartsExpanded} timeout="auto">
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Sales Pipeline
                </Typography>
                <Box sx={{ width: '100%', height: { xs: 250, sm: 300 }, overflowX: 'auto' }}>
                  <ResponsiveContainer>
                    <BarChart data={salesPipeline}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" style={{ fontSize: isMobile ? '10px' : '12px' }} />
                      <YAxis style={{ fontSize: isMobile ? '10px' : '12px' }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: isMobile ? '10px' : '12px' }} />
                      <Bar dataKey="count" name="Sales Count">
                        {salesPipeline.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Revenue Trend (Last 6 Months)
                </Typography>
                <Box sx={{ width: '100%', height: { xs: 250, sm: 300 }, overflowX: 'auto' }}>
                  <ResponsiveContainer>
                    <LineChart data={revenueData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" style={{ fontSize: isMobile ? '10px' : '12px' }} />
                      <YAxis style={{ fontSize: isMobile ? '10px' : '12px' }} />
                      <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                      <Legend wrapperStyle={{ fontSize: isMobile ? '10px' : '12px' }} />
                      <Line
                        type="monotone"
                        dataKey="revenue"
                        stroke="#003366"
                        strokeWidth={2}
                        dot={{ fill: '#D4AF37', r: 4 }}
                        name="Revenue"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* Third Row - Activity and Events */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Activity
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {recentActivity.length > 0 ? (
                <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {recentActivity.map((activity) => (
                    <ListItem key={activity.id} dense>
                      <ListItemIcon>{activity.icon}</ListItemIcon>
                      <ListItemText
                        primary={activity.title}
                        secondary={
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Chip label={activity.subtitle} size="small" />
                            <Typography variant="caption" color="text.secondary">
                              {activity.date ? format(parseISO(activity.date), 'MMM dd, yyyy') : 'N/A'}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center" py={3}>
                  No recent activity
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Upcoming Events
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {upcomingEventsList.length > 0 ? (
                <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {upcomingEventsList.map((event) => (
                    <ListItem key={event.id} dense>
                      <ListItemIcon>
                        <CalendarToday sx={{ color: 'secondary.main' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={event.title}
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              {event.start_time ? format(parseISO(event.start_time), 'MMM dd, yyyy hh:mm a') : 'TBD'}
                            </Typography>
                            {event.event_type && (
                              <Chip label={event.event_type} size="small" sx={{ mt: 0.5 }} />
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center" py={3}>
                  No upcoming events
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Fourth Row - Quick Actions */}
      <Grid container spacing={{ xs: 2, sm: 3 }} mb={{ xs: 2, sm: 3 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box display="flex" gap={{ xs: 1, sm: 2 }} flexWrap="wrap" mt={2}>
                <Button
                  variant="contained"
                  startIcon={!isMobile && <AddCircle />}
                  onClick={() => navigate('/sales/new')}
                  size={isMobile ? "small" : "medium"}
                  fullWidth={isMobile}
                >
                  {isMobile ? "New Sale" : "Create New Sale"}
                </Button>
                <Button
                  variant="contained"
                  startIcon={!isMobile && <PersonAdd />}
                  onClick={() => navigate('/leads/new')}
                  color="warning"
                  size={isMobile ? "small" : "medium"}
                  fullWidth={isMobile}
                >
                  {isMobile ? "Add Lead" : "Add Lead"}
                </Button>
                <Button
                  variant="contained"
                  startIcon={!isMobile && <PostAdd />}
                  onClick={() => navigate('/inventory/new')}
                  color="info"
                  size={isMobile ? "small" : "medium"}
                  fullWidth={isMobile}
                >
                  {isMobile ? "Add Item" : "Add Inventory"}
                </Button>
                <Button
                  variant="contained"
                  startIcon={!isMobile && <ReceiptLong />}
                  onClick={() => navigate('/invoices/new')}
                  color="success"
                  size={isMobile ? "small" : "medium"}
                  fullWidth={isMobile}
                >
                  {isMobile ? "Invoice" : "Create Invoice"}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Alerts and Widgets */}
      <Grid container spacing={3}>
        {alerts.lowInventory.length > 0 && (
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Badge badgeContent={alerts.lowInventory.length} color="warning">
                    <Warning sx={{ color: 'warning.main' }} />
                  </Badge>
                  <Typography variant="h6">Low Value Items</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  {alerts.lowInventory.map((item) => (
                    <ListItem key={item.id}>
                      <ListItemText
                        primary={item.name}
                        secondary={`$${parseFloat(item.price || 0).toLocaleString()}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}

        {alerts.overdueInvoices.length > 0 && (
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Badge badgeContent={alerts.overdueInvoices.length} color="error">
                    <Notifications sx={{ color: 'error.main' }} />
                  </Badge>
                  <Typography variant="h6">Overdue Invoices</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  {alerts.overdueInvoices.map((invoice) => (
                    <ListItem key={invoice.id}>
                      <ListItemText
                        primary={`Invoice #${invoice.id}`}
                        secondary={`$${parseFloat(invoice.total_amount || 0).toLocaleString()}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}

        {alerts.followUpLeads.length > 0 && (
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Badge badgeContent={alerts.followUpLeads.length} color="info">
                    <People sx={{ color: 'info.main' }} />
                  </Badge>
                  <Typography variant="h6">Follow-up Required</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  {alerts.followUpLeads.map((lead) => (
                    <ListItem key={lead.id}>
                      <ListItemText
                        primary={lead.full_name}
                        secondary={lead.email}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default Dashboard;
